import { formatMoney } from '@theme/money-formatting';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Frequently bought together bundle — pairs the current product with a selected product.
 */
class FrequentlyBoughtTogether extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Record<string, unknown> | null} */
  #mainVariant = null;

  /** @type {Record<string, unknown> | null} */
  #pairedVariant = null;

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    this.#mainVariant = this.#parseJson('data-main-variant');
    this.#pairedVariant = this.#getSelectedPairedVariant();

    const { signal } = (this.#abortController = new AbortController());

    const section = this.closest('.shopify-section, featured-product-information');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.addEventListener('change', this.#handleChange, { signal });
    this.querySelector('[data-fbt-buy-button]')?.addEventListener('click', this.#handleBuyTogether, { signal });

    this.#updateUI();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /** @param {Event} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    /** @type {import('@shopify/events').ProductSelectEvent} */
    const productEvent = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);

    productEvent.promise
      ?.then(({ detail }) => {
        if (detail?.productId && detail.productId !== this.dataset.mainProductId) return;

        const resource = detail?.resource;
        if (!resource || typeof resource !== 'object') return;

        this.#mainVariant = /** @type {Record<string, unknown>} */ (resource);
        this.#updateUI();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[frequently-bought-together] Product select failed:', error);
        }
      });
  };

  #handleChange = () => {
    this.#pairedVariant = this.#getSelectedPairedVariant();
    this.#updateUI();
  };

  /**
   * @returns {Record<string, unknown> | null}
   */
  #getSelectedPairedVariant() {
    const variants = this.#parseVariants();
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('[data-fbt-select]')));

    if (selects.length === 0) {
      return variants.find((variant) => variant.available) || variants[0] || null;
    }

    const selectedOptions = selects.map((select) => select.value);

    return (
      variants.find((variant) => {
        const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        if (optionValues.length < selectedOptions.length) return false;
        return selectedOptions.every((value, index) => optionValues[index] === value);
      }) ||
      variants.find((variant) => variant.available) ||
      null
    );
  }

  /**
   * @returns {Array<Record<string, unknown>>}
   */
  #parseVariants() {
    const script = this.querySelector('script[data-fbt-variants]');
    const json = script?.textContent?.trim();
    if (!json) return [];

    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} selector
   * @returns {Record<string, unknown> | null}
   */
  #parseJson(selector) {
    const script = this.querySelector(`script[${selector}]`);
    const json = script?.textContent?.trim();
    if (!json) return null;

    try {
      const parsed = JSON.parse(json);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {unknown} variant
   * @returns {number}
   */
  #getVariantPrice(variant) {
    return typeof variant?.price === 'number' ? variant.price : 0;
  }

  /**
   * @param {unknown} variant
   * @returns {number}
   */
  #getVariantCompareAt(variant) {
    const price = this.#getVariantPrice(variant);
    const compareAt = typeof variant?.compare_at_price === 'number' ? variant.compare_at_price : 0;
    return compareAt > price ? compareAt : price;
  }

  /**
   * @param {unknown} variant
   * @returns {{ src: string, alt: string } | null}
   */
  #getVariantImage(variant) {
    if (!variant || typeof variant !== 'object') return null;

    const featuredImage = /** @type {{ src?: string, alt?: string } | undefined} */ (variant.featured_image);
    if (featuredImage?.src) {
      return { src: featuredImage.src, alt: featuredImage.alt || '' };
    }

    const featuredMedia = /** @type {{ preview_image?: { src?: string, alt?: string } } | undefined} */ (
      variant.featured_media
    );
    if (featuredMedia?.preview_image?.src) {
      return { src: featuredMedia.preview_image.src, alt: featuredMedia.preview_image.alt || '' };
    }

    return null;
  }

  /**
   * @param {HTMLImageElement | null} imageEl
   * @param {unknown} variant
   */
  #updateVariantImage(imageEl, variant) {
    const image = this.#getVariantImage(variant);
    if (!(imageEl instanceof HTMLImageElement) || !image?.src) return;

    imageEl.src = `${image.src}&width=128`;
    if (image.alt) imageEl.alt = image.alt;
  }

  /**
   * @returns {string}
   */
  #getMainVariantId() {
    const section = this.closest('.shopify-section, featured-product-information');
    const variantInput = section?.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    return this.#mainVariant?.id ? String(this.#mainVariant.id) : '';
  }

  #formatPrice(cents) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    return formatMoney(cents, moneyFormat, currency);
  }

  #updateUI() {
    const mainVariant = this.#mainVariant;
    const pairedVariant = this.#pairedVariant;
    const buyButton = this.querySelector('[data-fbt-buy-button]');

    if (!mainVariant || !pairedVariant) {
      buyButton?.toggleAttribute('disabled', true);
      return;
    }

    const mainAvailable = mainVariant.available !== false;
    const pairedAvailable = pairedVariant.available !== false;
    buyButton?.toggleAttribute('disabled', !(mainAvailable && pairedAvailable));

    const pairedImage = this.querySelector('[data-fbt-paired-image]');
    const pairedPrice = this.querySelector('[data-fbt-paired-price]');
    const pairedCompare = this.querySelector('[data-fbt-paired-compare]');
    const bundlePrice = this.querySelector('[data-fbt-bundle-price]');
    const bundleCompare = this.querySelector('[data-fbt-bundle-compare]');

    this.#updateVariantImage(pairedImage, pairedVariant);

    const mainPrice = this.#getVariantPrice(mainVariant);
    const pairedPriceValue = this.#getVariantPrice(pairedVariant);
    const totalPrice = mainPrice + pairedPriceValue;
    const totalCompare = this.#getVariantCompareAt(mainVariant) + this.#getVariantCompareAt(pairedVariant);

    if (pairedPrice) {
      pairedPrice.textContent = this.#formatPrice(pairedPriceValue);
    }

    if (pairedCompare) {
      const pairedCompareValue = this.#getVariantCompareAt(pairedVariant);
      if (pairedCompareValue > pairedPriceValue) {
        pairedCompare.textContent = this.#formatPrice(pairedCompareValue);
        pairedCompare.hidden = false;
      } else {
        pairedCompare.hidden = true;
      }
    }

    if (bundlePrice) {
      bundlePrice.textContent = this.#formatPrice(totalPrice);
    }

    if (bundleCompare) {
      if (totalCompare > totalPrice) {
        bundleCompare.textContent = this.#formatPrice(totalCompare);
        bundleCompare.hidden = false;
      } else {
        bundleCompare.hidden = true;
      }
    }

    const mainImage = this.#getVariantImage(mainVariant);
    if (mainImage?.src) {
      this.dataset.productVariantMedia = `${mainImage.src}&width=100`;
    }
  }

  /**
   * @param {HTMLButtonElement} buyButton
   */
  #animateFlyToCart(buyButton) {
    if (this.dataset.addToCartAnimation !== 'true') return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !image) return;

    const flyToCartElement = /** @type {HTMLElement & { source: Element; destination: Element }} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.classList.add('fly-to-cart--main');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = buyButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * @param {HTMLButtonElement} buyButton
   */
  #animateAddToCart = async (buyButton) => {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    buyButton.dataset.added = 'true';

    await yieldToMainThread();
    await onAnimationEnd(buyButton);

    this.#resetTimeout = setTimeout(() => {
      buyButton.removeAttribute('data-added');
    }, 800);
  };

  #getMainQuantity() {
    const section = this.closest('.shopify-section, featured-product-information');
    const quantityInput = section?.querySelector('quantity-selector-component input[ref="quantityInput"]');
    return quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;
  }

  /** @param {Event} event */
  #handleBuyTogether = async (event) => {
    event.preventDefault();

    const pairedVariant = this.#getSelectedPairedVariant();
    const buyButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-fbt-buy-button]'));

    const mainVariantId = this.#getMainVariantId();
    const pairedVariantId = pairedVariant?.id ? String(pairedVariant.id) : '';

    if (!mainVariantId || !pairedVariantId || buyButton?.disabled) return;
    if (pairedVariant?.available === false) return;

    this.#animateFlyToCart(buyButton);
    this.#animateAddToCart(buyButton);

    buyButton.disabled = true;

    const quantity = this.#getMainQuantity();
    const items = [
      { variantId: mainVariantId, quantity },
      { variantId: pairedVariantId, quantity: 1 },
    ];

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.variantId),
            quantity: item.quantity,
          })),
          sections: sectionIds.join(','),
        }),
      });

      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: result.description,
              errors: result.errors,
            },
          })
        );

        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const ajaxCart = await cartResponse.json();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'frequently-bought-together',
            itemCount: quantity + 1,
          },
        });
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'frequently-bought-together',
          itemCount: quantity + 1,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[frequently-bought-together] Buy together failed:', error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      buyButton.disabled = false;
      this.#updateUI();
    }
  };
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
